<script setup lang="ts">
import type {
  InboxMessageProjection, OutboundMessageProjection, OwnerChannelsProjection, PhoneNumberProjection, PhoneNumberRequestProjection, SmsMessageProjection,
} from '@skiphr/contracts'
import { AtSign, Inbox, LoaderCircle, MessageSquareText, Phone, Send, ShieldCheck } from 'lucide-vue-next'
import { collection, doc, limit, onSnapshot, orderBy, query } from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'
import { storeToRefs } from 'pinia'
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { firebaseClient } from '@/firebase'
import { useAuthStore } from '@/stores/auth'
import { useUsageStore } from '@/stores/domain'

type Tab = 'inbox' | 'outbox' | 'numbers' | 'reach'
const tab = ref<Tab>('inbox')
const address = ref('')
const emails = ref<readonly InboxMessageProjection[]>([])
const texts = ref<readonly SmsMessageProjection[]>([])
const outbound = ref<readonly OutboundMessageProjection[]>([])
const numbers = ref<readonly PhoneNumberProjection[]>([])
const requests = ref<readonly PhoneNumberRequestProjection[]>([])
const owner = ref<OwnerChannelsProjection | null>(null)
const selected = ref<string>()
const busy = ref('')
const error = ref('')
const phone = ref('')
const code = ref('')
const codeSent = ref(false)
const { balance } = storeToRefs(useUsageStore())
const auth = useAuthStore()
let unsubscribers: (() => void)[] = []

interface InboxItem { id: string; kind: 'email' | 'sms'; from: string; to: string; title: string; body: string; codes: readonly string[]; links: readonly string[]; fromOwner: boolean; at: number }
const inboxItems = computed<InboxItem[]>(() => [
  ...emails.value.map((message) => ({ id: message.id, kind: 'email' as const, from: message.from, to: message.to, title: message.subject || '(no subject)',
    body: message.text, codes: message.codes, links: message.links, fromOwner: message.fromOwner, at: message.receivedAt })),
  ...texts.value.filter((message) => message.direction === 'inbound').map((message) => ({ id: message.id, kind: 'sms' as const, from: message.from, to: message.to,
    title: message.body.slice(0, 80), body: message.body, codes: message.codes, links: [], fromOwner: message.fromOwner, at: message.createdAt })),
].sort((a, b) => b.at - a.at))
const outboxItems = computed(() => [
  ...outbound.value.map((message) => ({ id: message.id, kind: message.channel, to: message.to, title: message.subject ?? message.body.slice(0, 80),
    body: message.body, status: message.status, at: message.sentAt ?? message.createdAt })),
  ...texts.value.filter((message) => message.direction === 'outbound' && !outbound.value.some((item) => item.channel === 'sms' && item.body === message.body))
    .map((message) => ({ id: message.id, kind: 'sms' as const, to: message.to, title: message.body.slice(0, 80), body: message.body, status: 'sent' as const, at: message.createdAt })),
].sort((a, b) => b.at - a.at))
const activeNumbers = computed(() => numbers.value.filter((number) => number.status === 'active'))
const pendingRequests = computed(() => requests.value.filter((request) => request.status === 'awaiting_approval'))
const stamp = (value: number) => new Date(value).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
const credits = (value: number) => value.toLocaleString('en-US')

onMounted(async () => {
  const uid = auth.user?.uid
  if (!uid) return
  const watch = <T>(path: string, field: string, target: { value: readonly T[] }) => onSnapshot(
    query(collection(firebaseClient.firestore, path, uid, 'items'), orderBy(field, 'desc'), limit(100)),
    (snapshot) => { target.value = snapshot.docs.map((item) => item.data() as T) })
  unsubscribers = [
    watch('inboxMessages', 'receivedAt', emails), watch('smsMessages', 'createdAt', texts), watch('outboundMessages', 'createdAt', outbound),
    watch('phoneNumbers', 'createdAt', numbers), watch('phoneNumberRequests', 'createdAt', requests),
    onSnapshot(doc(firebaseClient.firestore, 'ownerChannels', uid), (snapshot) => { owner.value = snapshot.exists() ? snapshot.data() as OwnerChannelsProjection : null }),
  ]
  try { address.value = (await httpsCallable<unknown, { address: string }>(firebaseClient.functions, 'getManagerInbox')({})).data.address }
  catch { address.value = '' }
})
onBeforeUnmount(() => unsubscribers.forEach((unsubscribe) => unsubscribe()))

async function call(name: string, data: Record<string, unknown>, key: string, after?: () => void): Promise<void> {
  busy.value = key; error.value = ''
  try { await httpsCallable(firebaseClient.functions, name)(data); after?.() }
  catch (caught) { error.value = caught instanceof Error ? caught.message.replace('Firebase: ', '') : 'That action failed.' }
  finally { busy.value = '' }
}
</script>

<template><div class="section-page">
  <header class="section-page__header"><div><span class="eyebrow">Communications</span><h1>Inbox &amp; outbox</h1>
    <p>Email and texts your Manager receives, and the replies it sends you. Agents use this inbox to sign up for accounts and read verification codes.</p></div></header>
  <section class="detail-grid">
    <article class="detail-card"><div class="detail-card__head"><div><span class="eyebrow">Manager email</span><h2 class="comms-address">{{ address || 'Setting up…' }}</h2></div><AtSign :size="18" /></div>
      <p>Email your Manager from {{ owner?.email || 'your account email' }} and it replies by email. Agents use plus-addresses such as <code>{{ address ? address.replace('@', '+service@') : 'name+service@…' }}</code> for sign-ups.</p></article>
    <article class="detail-card"><div class="detail-card__head"><div><span class="eyebrow">Phone numbers</span><h2>{{ activeNumbers.length ? activeNumbers.map((number) => number.phoneNumber).join(', ') : 'None yet' }}</h2></div><Phone :size="18" /></div>
      <p>Numbers receive MFA codes and your texts to the Manager. Your Manager asks before buying one, and you approve the cost.</p></article>
  </section>
  <p v-if="error" class="form-error" role="alert">{{ error }}</p>
  <nav class="comms-tabs" role="tablist">
    <button v-for="item in ([['inbox', 'Inbox'], ['outbox', 'Outbox'], ['numbers', `Phone numbers${pendingRequests.length ? ` (${pendingRequests.length})` : ''}`], ['reach', 'Reach your Manager']] as const)"
      :key="item[0]" type="button" role="tab" :aria-selected="tab === item[0]" :class="{ active: tab === item[0] }" @click="tab = item[0]">{{ item[1] }}</button>
  </nav>

  <section v-if="tab === 'inbox'" class="detail-card detail-card--wide">
    <div v-if="inboxItems.length" class="comms-list">
      <article v-for="item in inboxItems" :key="item.id" class="comms-item" :class="{ open: selected === item.id }" @click="selected = selected === item.id ? undefined : item.id">
        <span class="comms-item__icon"><Inbox v-if="item.kind === 'email'" :size="15" /><MessageSquareText v-else :size="15" /></span>
        <div class="comms-item__main">
          <div class="comms-item__head"><strong>{{ item.from }}</strong><small>{{ stamp(item.at) }}</small></div>
          <span class="comms-item__title">{{ item.title }}<em v-if="item.fromOwner"> · from you</em></span>
          <div v-if="item.codes.length" class="comms-codes"><span v-for="value in item.codes" :key="value">{{ value }}</span></div>
          <div v-if="selected === item.id" class="comms-item__body"><small>To {{ item.to }}</small><pre>{{ item.body }}</pre>
            <a v-for="link in item.links.slice(0, 3)" :key="link" :href="link" target="_blank" rel="noopener noreferrer nofollow">{{ link }}</a></div>
        </div>
      </article>
    </div>
    <p v-else>Nothing received yet.</p>
  </section>

  <section v-else-if="tab === 'outbox'" class="detail-card detail-card--wide">
    <div v-if="outboxItems.length" class="comms-list">
      <article v-for="item in outboxItems" :key="item.id" class="comms-item" :class="{ open: selected === item.id }" @click="selected = selected === item.id ? undefined : item.id">
        <span class="comms-item__icon"><Send :size="15" /></span>
        <div class="comms-item__main">
          <div class="comms-item__head"><strong>To {{ item.to }}</strong><small>{{ item.kind === 'email' ? 'Email' : 'Text' }} · {{ item.status }} · {{ stamp(item.at) }}</small></div>
          <span class="comms-item__title">{{ item.title }}</span>
          <div v-if="selected === item.id" class="comms-item__body"><pre>{{ item.body }}</pre></div>
        </div>
      </article>
    </div>
    <p v-else>Your Manager hasn't sent anything yet.</p>
  </section>

  <section v-else-if="tab === 'numbers'" class="detail-card detail-card--wide">
    <div v-for="request in pendingRequests" :key="request.id" class="comms-request">
      <div><strong>Your Manager wants a phone number</strong><p>{{ request.purpose }}</p>
        <p class="comms-cost">{{ credits(request.monthlyCredits) }} credits a month + {{ credits(request.messageCredits) }} credits per text · {{ request.country }}{{ request.areaCode ? ` · area code ${request.areaCode}` : '' }} · your balance {{ credits(Math.round(balance)) }}</p></div>
      <div class="connection-actions">
        <button class="button button--primary button--small" type="button" :disabled="Boolean(busy)" @click="call('resolvePhoneNumberRequest', { requestId: request.id, approve: true }, request.id)">
          <LoaderCircle v-if="busy === request.id" :size="15" class="spin" /><ShieldCheck v-else :size="15" /> Approve &amp; buy</button>
        <button class="button button--ghost button--small" type="button" :disabled="Boolean(busy)" @click="call('resolvePhoneNumberRequest', { requestId: request.id, approve: false }, `${request.id}-no`)">Decline</button>
      </div>
    </div>
    <div v-if="activeNumbers.length" class="team-list"><div v-for="number in activeNumbers" :key="number.id"><span><strong>{{ number.phoneNumber }}</strong><small>{{ number.purpose }} · {{ credits(number.monthlyCredits) }} credits a month · renews {{ stamp(number.nextChargeAt) }}</small></span>
      <button class="button button--ghost button--small" type="button" :disabled="Boolean(busy)" @click="call('releasePhoneNumber', { phoneNumberId: number.id }, number.id)">Release</button></div></div>
    <p v-if="!pendingRequests.length && !activeNumbers.length">No phone numbers. When a task needs one, for example to receive an MFA code, your Manager asks here first and shows the cost.</p>
  </section>

  <section v-else class="detail-card detail-card--wide">
    <h2>Text your Manager</h2>
    <p>Verify your mobile number, then text any of your Manager's numbers and it replies by text. Each text costs 2 credits.</p>
    <p v-if="owner?.phoneVerified" class="form-success">Verified: {{ owner.phone }}</p>
    <div class="comms-verify">
      <input v-model="phone" type="tel" autocomplete="tel" placeholder="+15551234567" />
      <button class="button button--dark button--small" type="button" :disabled="!phone || Boolean(busy)" @click="call('setOwnerPhone', { phone }, 'phone', () => { codeSent = true })">Send code</button>
    </div>
    <p class="sms-consent">By tapping Send code, you agree to receive text messages from SkipHR at this number, including verification codes and messages from your Manager about your work. Message frequency varies. Message and data rates may apply. Reply STOP to opt out or HELP for help. Consent is not a condition of purchase. See our <RouterLink to="/terms#sms">SMS terms</RouterLink> and <RouterLink to="/privacy">Privacy Policy</RouterLink>.</p>
    <div v-if="codeSent" class="comms-verify">
      <input v-model="code" inputmode="numeric" autocomplete="one-time-code" placeholder="6-digit code" />
      <button class="button button--primary button--small" type="button" :disabled="code.length < 6 || Boolean(busy)" @click="call('verifyOwnerPhone', { code }, 'verify', () => { codeSent = false; code = '' })">Verify</button>
    </div>
  </section>
</div></template>
